import { useState, useCallback, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MapView from './MapView';
import Toolbar from './Toolbar';
import LinesPanel from './LinesPanel';
import useTheme from '../admindashboard/useTheme.js';
import './mappoint.css';

const DARK = {
  accent: '#facc15',
  accentSoft: 'rgba(250, 204, 21, 0.7)',
  accentBg: 'rgba(250, 204, 21, 0.12)',
  accentBgHover: 'rgba(250, 204, 21, 0.16)',
  accentOnBg: '#0f172a',
  aura1: '#facc15',
  aura2: '#3b82f6',
  pageBg: '#0b1220',
  containerBg: '#0b1220',
  pageEdge: 'rgba(255,255,255,0.10)',
  glassBg: 'linear-gradient(135deg, rgba(30, 58, 138, 0.55) 0%, rgba(23, 37, 84, 0.5) 50%, rgba(30, 64, 175, 0.55) 100%)',
  glassInner: 'linear-gradient(135deg, rgba(30, 64, 175, 0.35) 0%, rgba(23, 37, 84, 0.3) 100%)',
  glassBorder: 'rgba(250, 204, 21, 0.18)',
  glassInnerBorder: 'rgba(96, 165, 250, 0.14)',
  textPrimary: '#ffffff',
  textSecondary: 'rgba(226, 232, 240, 0.7)',
  textMuted: 'rgba(148, 163, 184, 0.6)',
  inputBg: 'rgba(255, 255, 255, 0.10)',
  inputBorder: 'rgba(255, 255, 255, 0.15)',
  btnGhostBg: 'rgba(255, 255, 255, 0.10)',
  btnGhostBgHover: 'rgba(255, 255, 255, 0.15)',
  btnGhostBorder: 'rgba(255, 255, 255, 0.15)',
  divider: 'rgba(255, 255, 255, 0.10)',
  scrollTrack: 'rgba(30, 58, 138, 0.3)',
  scrollThumb: 'rgba(250, 204, 21, 0.5)',
  scrollThumbHover: 'rgba(250, 204, 21, 0.7)',
};

const LIGHT = {
  accent: '#2563eb',
  accentSoft: 'rgba(37, 99, 235, 0.75)',
  accentBg: 'rgba(37, 99, 235, 0.10)',
  accentBgHover: 'rgba(37, 99, 235, 0.14)',
  accentOnBg: '#ffffff',
  aura1: '#60a5fa',
  aura2: '#c084fc',
  pageBg: '#f7f9fc',
  containerBg: '#ffffff',
  pageEdge: 'rgba(15, 23, 42, 0.10)',
  glassBg: 'linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(248, 250, 252, 0.8) 50%, rgba(239, 246, 255, 0.85) 100%)',
  glassInner: 'linear-gradient(135deg, rgba(239, 246, 255, 0.7) 0%, rgba(241, 245, 249, 0.7) 100%)',
  glassBorder: 'rgba(37, 99, 235, 0.18)',
  glassInnerBorder: 'rgba(148, 163, 184, 0.20)',
  textPrimary: '#0f172a',
  textSecondary: 'rgba(51, 65, 85, 0.75)',
  textMuted: 'rgba(100, 116, 139, 0.65)',
  inputBg: 'rgba(255, 255, 255, 0.9)',
  inputBorder: 'rgba(148, 163, 184, 0.30)',
  btnGhostBg: 'rgba(15, 23, 42, 0.05)',
  btnGhostBgHover: 'rgba(15, 23, 42, 0.09)',
  btnGhostBorder: 'rgba(15, 23, 42, 0.10)',
  divider: 'rgba(15, 23, 42, 0.10)',
  scrollTrack: 'rgba(226, 232, 240, 0.6)',
  scrollThumb: 'rgba(37, 99, 235, 0.4)',
  scrollThumbHover: 'rgba(37, 99, 235, 0.6)',
};

function MapPointIntroCurtain({ show, isLight, C }) {
  const tileCls = isLight
    ? 'relative grid size-20 place-items-center rounded-[28px] p-2.5 border bg-white'
    : 'relative grid size-20 place-items-center rounded-[28px] bg-white shadow-lifted p-2.5 border';
  const tileStyle = {
    borderColor: isLight ? 'rgba(15,23,42,0.08)' : undefined,
    boxShadow: isLight
      ? '0 2px 4px rgba(15,23,42,0.08), 0 24px 48px -20px rgba(15,23,42,0.22)'
      : undefined,
  };
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="curtain"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.06, filter: 'blur(10px)' }}
          transition={{ duration: 0.75, ease: [0.65, 0, 0.35, 1] }}
          className={isLight
            ? 'fixed inset-0 z-[120] grid place-items-center'
            : 'routecore-app map-dots fixed inset-0 z-[120] grid place-items-center bg-paper'}
          style={
            isLight
              ? { background: '#ffffff', color: '#0f172a' }
              : undefined
          }
        >
          <div className="flex flex-col items-center">
            <motion.span
              initial={{ opacity: 0, scale: 0.6, rotate: -22 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 150, damping: 13, delay: 0.05 }}
              className={tileCls}
              style={tileStyle}
            >
              <motion.span
                className="absolute inset-0 rounded-[28px] border-2"
                style={{ borderColor: C.accent }}
                initial={{ opacity: 0.9, scale: 1 }}
                animate={{ opacity: 0, scale: 1.55 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeOut' }}
              />
              <img src="/iSource LOGO1.png" alt="iSource" className="w-full h-full object-contain" />
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className={isLight ? 'mt-6 font-display text-3xl font-semibold tracking-tight text-slate-900' : 'mt-6 font-display text-3xl font-semibold tracking-tight'}
            >
              iSource MapPoint
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}
              className={isLight ? 'mt-2 text-[10px] font-bold tracking-[0.34em] text-slate-500 uppercase' : 'mt-2 text-[10px] font-bold tracking-[0.34em] text-ink-faint uppercase'}
            >
              Geospatial Annotation Studio
            </motion.p>

            <div
              className="relative mt-8 h-px w-56 overflow-hidden"
              style={{ background: isLight ? 'rgba(15,23,42,0.12)' : undefined }}
            >
              <motion.span
                className="absolute inset-y-0 left-0"
                style={{ background: C.accent }}
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 1.15, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
              />
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className={isLight ? 'mt-3 font-display text-xs text-slate-500 italic' : 'mt-3 font-display text-xs text-ink-faint italic'}
            >
              Loading map tiles…
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function MapPointApp({ axiosInstance }) {
  const { isLight } = useTheme();
  const C = isLight ? LIGHT : DARK;

  const [lines, setLines] = useState([]);
  const [currentPoints, setCurrentPoints] = useState([]);
  const [selectedColor, setSelectedColor] = useState('#ef4444');
  const [selectedWeight, setSelectedWeight] = useState(4);
  const [currentTag, setCurrentTag] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [minElapsed, setMinElapsed] = useState(false);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), 1250);
    return () => clearTimeout(t);
  }, []);

  const booting = !minElapsed;

  // Refs so the drawing-toggle callback always sees the latest values.
  const pointsRef = useRef(currentPoints);
  const colorRef = useRef(selectedColor);
  const weightRef = useRef(selectedWeight);
  const tagRef = useRef(currentTag);
  useEffect(() => { pointsRef.current = currentPoints; }, [currentPoints]);
  useEffect(() => { colorRef.current = selectedColor; }, [selectedColor]);
  useEffect(() => { weightRef.current = selectedWeight; }, [selectedWeight]);
  useEffect(() => { tagRef.current = currentTag; }, [currentTag]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get('/mappoint/lines');
        if (active) setLines(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        // Leave lines empty on failure.
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [axiosInstance]);

  const persistLine = useCallback(async (points) => {
    try {
      const res = await axiosInstance.post('/mappoint/lines', {
        points,
        color: colorRef.current,
        tag: tagRef.current,
        weight: weightRef.current,
        visible: true,
      });
      setLines((prev) => [...prev, res.data]);
    } catch (e) {
      // ignore
    }
  }, [axiosInstance]);

  const handleMapReady = useCallback((map) => {
    mapInstanceRef.current = map;
  }, []);

  const handleMapClick = useCallback((latlng) => {
    if (!isDrawing) return;
    setCurrentPoints((prev) => [...prev, [latlng.lat, latlng.lng]]);
  }, [isDrawing]);

  const handleToggleDrawing = useCallback(() => {
    setIsDrawing((prev) => {
      if (prev && pointsRef.current.length >= 2) {
        persistLine([...pointsRef.current]);
        setCurrentPoints([]);
        setCurrentTag('');
      } else if (prev) {
        setCurrentPoints([]);
      }
      return !prev;
    });
  }, [persistLine]);

  const handleFinishLine = useCallback(() => {
    if (currentPoints.length < 2) return;
    persistLine([...currentPoints]);
    setCurrentPoints([]);
    setCurrentTag('');
  }, [currentPoints, persistLine]);

  const handleCancelLine = useCallback(() => setCurrentPoints([]), []);
  const handleUndo = useCallback(() => setCurrentPoints((prev) => prev.slice(0, -1)), []);

  const handleToggleVisibility = useCallback((id) => {
    let nextVisible = true;
    setLines((prev) => prev.map((l) => {
      if (l.id === id) { nextVisible = !l.visible; return { ...l, visible: nextVisible }; }
      return l;
    }));
    axiosInstance.put(`/mappoint/lines/${id}`, { visible: nextVisible }).catch(() => {});
  }, [axiosInstance]);

  const handleDeleteLine = useCallback((id) => {
    setLines((prev) => prev.filter((l) => l.id !== id));
    axiosInstance.delete(`/mappoint/lines/${id}`).catch(() => {});
  }, [axiosInstance]);

  const handleClearAll = useCallback(() => {
    setLines([]);
    axiosInstance.delete('/mappoint/lines').catch(() => {});
  }, [axiosInstance]);

  const handleZoomToLine = useCallback((line) => {
    if (!mapInstanceRef.current || line.points.length === 0) return;
    const bounds = L.latLngBounds(line.points);
    mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
  }, []);

  const tagTimers = useRef({});
  const handleEditTag = useCallback((id, newTag) => {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, tag: newTag } : l)));
    if (tagTimers.current[id]) clearTimeout(tagTimers.current[id]);
    tagTimers.current[id] = setTimeout(() => {
      axiosInstance.put(`/mappoint/lines/${id}`, { tag: newTag }).catch(() => {});
    }, 500);
  }, [axiosInstance]);

  return (
    <>
      <MapPointIntroCurtain show={booting} isLight={isLight} C={C} />

      <motion.div
        initial={{ opacity: 0, scale: 0.985 }}
        animate={{ opacity: booting ? 0 : 1, scale: booting ? 0.985 : 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={
          isLight
            ? 'mappoint-app relative w-full rounded-2xl overflow-hidden border font-[\'Inter\'] shadow-2xl antialiased'
            : 'mappoint-app relative w-full rounded-2xl overflow-hidden border border-white/[0.08] font-[\'Inter\'] shadow-2xl antialiased'
        }
        data-testid="mappoint-app"
        style={{
          height: 'calc(100vh - 190px)',
          minHeight: '540px',
          background: C.pageBg,
          borderColor: isLight ? C.pageEdge : undefined,
          color: C.textPrimary,
        }}
      >
        <div className="pointer-events-none absolute inset-0 z-[1]">
          <div
            className="aura-1 absolute -left-40 -top-40 h-[560px] w-[560px] rounded-full opacity-[0.07]"
            style={{ background: `radial-gradient(circle, ${C.aura1} 0%, transparent 65%)` }}
          />
          <div
            className="aura-2 absolute -bottom-52 -right-40 h-[640px] w-[640px] rounded-full opacity-[0.08]"
            style={{ background: `radial-gradient(circle, ${C.aura2} 0%, transparent 65%)` }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: isLight
                ? 'radial-gradient(ellipse 90% 80% at 50% 45%, transparent 55%, rgba(15,23,42,0.08) 100%)'
                : 'radial-gradient(ellipse 90% 80% at 50% 45%, transparent 55%, rgba(0,0,0,0.55) 100%)',
            }}
          />
        </div>

        <div className="w-full h-full relative z-[2]">
          <MapView
            lines={lines}
            currentPoints={currentPoints}
            currentColor={selectedColor}
            currentWeight={selectedWeight}
            isDrawing={isDrawing}
            onMapClick={handleMapClick}
            onMapReady={handleMapReady}
          />
        </div>

        <Toolbar
          isDrawing={isDrawing}
          selectedColor={selectedColor}
          selectedWeight={selectedWeight}
          currentTag={currentTag}
          hasCurrentPoints={currentPoints.length > 0}
          onToggleDrawing={handleToggleDrawing}
          onColorChange={setSelectedColor}
          onWeightChange={setSelectedWeight}
          onTagChange={setCurrentTag}
          onFinishLine={handleFinishLine}
          onCancelLine={handleCancelLine}
          onUndo={handleUndo}
          isLight={isLight}
          C={C}
        />

        <LinesPanel
          lines={lines}
          isOpen={isPanelOpen}
          onToggle={() => setIsPanelOpen((prev) => !prev)}
          onToggleVisibility={handleToggleVisibility}
          onDeleteLine={handleDeleteLine}
          onZoomToLine={handleZoomToLine}
          onEditTag={handleEditTag}
          isLight={isLight}
          C={C}
        />

        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[500]">
          <div
            className="glass-card rounded-full px-5 py-2 flex items-center gap-4 text-xs"
            style={{
              background: C.glassBg,
              border: `1px solid ${C.glassBorder}`,
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              boxShadow: isLight
                ? '0 4px 16px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)'
                : '0 8px 32px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
            }}
          >
            <div className="flex items-center gap-2" style={{ color: C.accentSoft }}>
              <div
                className="w-3 h-3 rounded-full border-2"
                style={{
                  backgroundColor: selectedColor,
                  borderColor: isLight ? 'rgba(15, 23, 42, 0.15)' : 'rgba(255,255,255,0.30)',
                }}
              />
              <span className="font-medium" style={{ color: C.textPrimary }}>
                {isDrawing ? `Drawing (${currentPoints.length} points)` : 'Navigate Mode'}
              </span>
            </div>
            <div className="w-px h-4" style={{ background: C.divider }} />
            <div style={{ color: C.textSecondary }}>
              {loading ? 'Loading…' : `${lines.length} line${lines.length !== 1 ? 's' : ''} saved`}
            </div>
            {isDrawing && (
              <>
                <div className="w-px h-4" style={{ background: C.divider }} />
                <div className="font-medium flex items-center gap-1" style={{ color: C.accent }}>
                  <span
                    className="w-1.5 h-1.5 rounded-full animate-pulse"
                    style={{ backgroundColor: C.accent, boxShadow: `0 0 8px ${C.accent}` }}
                  />
                  Click map to add points
                </div>
              </>
            )}
          </div>
        </div>

      </motion.div>
    </>
  );
}
